/**
 * Red Dog bot. Casual play: a modest ante (a small multiple of the minimum, never more than a tenth of the stack).
 * Raising pays only when the spread is wide: with seven ranks or more between the cards the third card is more likely
 * to land inside than not, so stronger bots raise there and stay otherwise. Beginners raise on a gut feeling instead.
 * The bot only ever reads its own seat's view.
 */
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import type { RedDogAction, RedDogLegal, RedDogView } from './engine.js';

function decide(legal: RedDogLegal, ctx: BotContext): RedDogAction {
  if (legal.kind !== 'decide' || legal.maxRaise < 1) return { type: 'stay' };
  if (ctx.difficulty === 'BEGINNER') return chance(ctx, 0.35) ? { type: 'raise', amount: legal.maxRaise } : { type: 'stay' };
  if (ctx.difficulty === 'EASY') return legal.spread >= 6 && chance(ctx, 0.7) ? { type: 'raise', amount: legal.maxRaise } : { type: 'stay' };
  return legal.spread >= 7 ? { type: 'raise', amount: legal.maxRaise } : { type: 'stay' };
}

export const redDogBot: BotEngine<RedDogView, RedDogLegal, RedDogAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'decide') return decide(legal, ctx);
    return this.chooseBet!(view, legal, ctx);
  },

  chooseBet(view, legal, ctx) {
    if (legal.kind !== 'bet') return this.handleTimeout(view, legal, ctx);
    if (legal.canSitOut && chance(ctx, 0.03)) return { type: 'sitOut' };
    const me = view.seat === null ? null : view.players[view.seat];
    const multiple = 1 + ctx.rng.int(ctx.difficulty === 'BEGINNER' ? 4 : 3);
    const cap = me ? Math.max(legal.min, Math.floor(me.chips / 10)) : legal.min;
    return { type: 'bet', amount: Math.max(legal.min, Math.min(legal.min * multiple, cap, legal.max)) };
  },

  evaluateState(view) {
    const me = view.seat === null ? null : view.players[view.seat];
    return me ? Math.max(0, Math.min(1, me.chips / (view.config.minBet * 200))) : 0;
  },

  calculateRisk() {
    return 0;
  },

  handleTimeout(view) {
    return view.phase === 'DECIDING' ? { type: 'stay' } : { type: 'sitOut' };
  },
};
